"use client";

import { useCallback, useMemo } from "react";
import type { DiffDisplayMode } from "@/app/[lang]/[workspaceId]/_components/table/diff-cell";
import { logger } from "@/lib/logger";
import { useLocalStorage } from "@/lib/storage/use-local-storage";

/**
 * @file 差分表示モードのLocalStorage永続化フック
 *
 * 検索アナリティクス（SA）およびユーザーアナリティクス（UA）の
 * 差分表示モード（実数/割合）をLocalStorageに保存・復元するためのカスタムフック。
 *
 * 機能:
 * - プロジェクトID別に表示モードを永続化
 * - SA/UA独立して状態管理
 * - SSR対応（useEffectでクライアントサイドのみアクセス）
 * - エラーハンドリング（LocalStorage無効時の graceful fallback）
 */

// Re-export for backwards compatibility
export type { DiffDisplayMode };

/** LocalStorageのキー名 */
const LOCAL_STORAGE_KEY = "amethyst:diffDisplayMode";

/** データ構造のバージョン */
const CURRENT_VERSION = 1;

/** LocalStorageに保存される状態の型定義 */
type StoredState = {
  sa: Record<string, DiffDisplayMode>;
  ua: Record<string, DiffDisplayMode>;
};

/** アプリケーションタイプ */
type AppType = "sa" | "ua";

/**
 * 差分表示モードのカスタムフック
 *
 * @param appType アプリケーションタイプ（"sa" または "ua"）
 * @param projectId プロジェクトID
 * @returns {mode, setMode} - 現在のモードと更新関数
 *
 * @example
 * ```tsx
 * const { mode, setMode } = useDiffDisplayMode("sa", projectId);
 *
 * // トグルで切り替え
 * const handleToggle = () => {
 *   setMode(mode === "absolute" ? "percentage" : "absolute");
 * };
 * ```
 */
export const useDiffDisplayMode = (
  appType: AppType,
  projectId: string,
): {
  mode: DiffDisplayMode;
  setMode: (mode: DiffDisplayMode) => void;
} => {
  // useLocalStorageで全体の状態を管理
  const [storedState, setStoredState] = useLocalStorage<StoredState>(
    LOCAL_STORAGE_KEY,
    {
      defaultValue: {
        sa: {},
        ua: {},
      },
      version: CURRENT_VERSION,
    },
  );

  // 現在のアプリケーション・プロジェクトのモードを取得
  const mode = useMemo(() => {
    const appData = storedState?.[appType];
    const projectData = appData?.[projectId];
    const result = projectData || "absolute";
    return result;
  }, [storedState, appType, projectId]);

  // モード更新関数
  const setMode = useCallback(
    (newMode: DiffDisplayMode) => {
      setStoredState((prev) => {
        const defaultState = { sa: {}, ua: {} };
        const currentState = prev || defaultState;

        // 新しい状態オブジェクトを作成
        const newState = {
          ...currentState,
          [appType]: {
            ...(currentState[appType] || {}),
            [projectId]: newMode,
          },
        };

        return newState;
      });
    },
    [setStoredState, appType, projectId],
  );

  return {
    mode,
    setMode,
  };
};

/**
 * 全ての差分表示モード設定をクリア（デバッグ用）
 */
export const clearAllDiffDisplayModes = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (error) {
    logger.error("Error clearing diff display modes:", error);
  }
};
